import {
  format,
  addYears,
  subMonths,
  subYears,
  startOfYear,
  endOfYear,
  endOfMonth,
} from "date-fns";
import { getOrgTimezone } from "@/utils/dateUtils";

/**
 * Period presets for the analytics explorer. The period drives ranged metrics
 * (revenue, activities, events…); the separate "as of" date drives
 * point-in-time counts (headcount, active contracts).
 */
export type PeriodPresetId =
  | "all"
  | "running_year"
  | "this_month"
  | "last_month"
  | "last_3_months"
  | "this_year"
  | "next_year"
  | "last_year"
  | "custom";

export const PERIOD_PRESETS: PeriodPresetId[] = [
  "all",
  "running_year",
  "this_month",
  "last_month",
  "last_3_months",
  "this_year",
  "next_year",
  "last_year",
  "custom",
];

/**
 * "Next year" is a forward window: only the Finance & Forecast silo has data
 * there (the projection, #2174). Every other silo reads history.
 */
const FORWARD_ONLY_PRESETS: PeriodPresetId[] = ["next_year"];

/** Kennzahlen metrics whose detail can show the next year (#2175, on #2174's preset). */
export const FORWARD_YEAR_METRICS = ["forecast"];

/**
 * The presets a silo offers and accepts from the URL. On the Kennzahlen silo
 * the year-end forecast's detail (`metric`) also takes "Next year", so its year
 * control and the period bar are one state, as on the forecast silo.
 */
export function presetsForSilo(silo: string, metric?: string | null): PeriodPresetId[] {
  const forward = silo === "forecast" || (silo === "metrics" && !!metric && FORWARD_YEAR_METRICS.includes(metric));
  return forward ? PERIOD_PRESETS : PERIOD_PRESETS.filter((p) => !FORWARD_ONLY_PRESETS.includes(p));
}

/** Floor for the "All time" range — the system's first billing year (MinBillingYear). */
const ALL_TIME_FROM = "2017-01";

export const DEFAULT_PERIOD: PeriodPresetId = "this_month";

export interface ResolvedPeriod {
  /** Inclusive start month, `YYYY-MM`. */
  from: string;
  /** Inclusive end month, `YYYY-MM`. */
  to: string;
  /** Point-in-time snapshot date, `YYYY-MM-DD`. */
  asOf: string;
}

export interface PeriodInput {
  from?: string;
  to?: string;
  asOf?: string;
}

const MONTH = "yyyy-MM";
const DAY = "yyyy-MM-dd";

/**
 * Resolves a preset (plus any custom from/to and an optional explicit "as of"
 * date) into concrete `{ from, to, asOf }` strings. Pure — pass `now` for
 * deterministic tests.
 *
 * `asOf` defaults to today (a "current snapshot"); the user can override it to
 * verify historical point-in-time counts. The period range is independent of
 * `asOf`.
 */
export function resolvePeriod(
  preset: PeriodPresetId,
  input: PeriodInput = {},
  now: Date = new Date(),
): ResolvedPeriod {
  const today = format(now, DAY);
  const asOf = input.asOf || today;

  switch (preset) {
    case "all": {
      // "All time" — a concrete wide range floored to the first billing year so
      // ranged-metric silos can query it. (The Finance & Forecast silo treats
      // "all" as "no zoom" client-side so its forward projection isn't clipped.)
      return { from: ALL_TIME_FROM, to: format(now, MONTH), asOf };
    }
    case "running_year": {
      // "Running year" — the trailing 12 months ending this month (the same
      // rolling window the reconciliation screen calls "Running year"). The
      // current month + the 11 prior months = 12 inclusive buckets.
      return {
        from: format(subMonths(now, 11), MONTH),
        to: format(now, MONTH),
        asOf,
      };
    }
    case "this_month": {
      const m = format(now, MONTH);
      return { from: m, to: m, asOf };
    }
    case "last_month": {
      const prev = subMonths(now, 1);
      const m = format(prev, MONTH);
      return { from: m, to: m, asOf };
    }
    case "last_3_months": {
      // The phone's "Last 3 months" (#2175 B5): this month and the two before.
      return { from: format(subMonths(now, 2), MONTH), to: format(now, MONTH), asOf };
    }
    case "this_year": {
      return {
        from: format(startOfYear(now), MONTH),
        to: format(endOfYear(now), MONTH),
        asOf,
      };
    }
    case "next_year": {
      const ny = addYears(now, 1);
      return {
        from: format(startOfYear(ny), MONTH),
        to: format(endOfYear(ny), MONTH),
        asOf,
      };
    }
    case "last_year": {
      const ly = subYears(now, 1);
      return {
        from: format(startOfYear(ly), MONTH),
        to: format(endOfYear(ly), MONTH),
        asOf,
      };
    }
    case "custom": {
      // Sanitize URL-supplied custom bounds: garbage like `?from=abc` / `2026-13`
      // must not reach `parseMonth` (which throws) and crash the panels (B10).
      // Fall back to the current month, and swap an inverted `from > to` range so
      // the contract `from ≤ to` always holds.
      const thisMonth = format(now, MONTH);
      const from = isValidMonth(input.from) ? input.from : thisMonth;
      const to = isValidMonth(input.to) ? input.to : from;
      return from <= to ? { from, to, asOf } : { from: to, to: from, asOf };
    }
  }
}

/** True when `month` is a well-formed `YYYY-MM` string with a 01–12 month. */
export function isValidMonth(month: string | undefined | null): month is string {
  if (!month) return false;
  const match = MONTH_RE.exec(month);
  if (!match) return false;
  const m = Number(match[2]);
  return m >= 1 && m <= 12;
}

/**
 * The first and last day of a `YYYY-MM` month, as `YYYY-MM-DD` — convenient for
 * endpoints that take day bounds (activities, events, invoices) from a period.
 */
export function monthDayBounds(fromMonth: string, toMonth: string): {
  startDate: string;
  endDate: string;
} {
  const start = parseMonth(fromMonth);
  const end = parseMonth(toMonth);
  return {
    startDate: format(start, DAY),
    endDate: format(endOfMonth(end), DAY),
  };
}

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The offset (timezone − UTC) in milliseconds for `timeZone` at `instant`,
 * derived DST-safely from `Intl` (no timezone library required).
 */
function tzOffsetMs(timeZone: string, instant: Date): number {
  const utc = new Date(instant.toLocaleString("en-US", { timeZone: "UTC" }));
  const zoned = new Date(instant.toLocaleString("en-US", { timeZone }));
  return zoned.getTime() - utc.getTime();
}

/**
 * Convert a wall-clock time in `timeZone` to the corresponding UTC `Date`.
 * The offset depends on the instant (DST), so we compute it from a
 * wall-clock-as-UTC guess and correct once — exact for any time outside the
 * ~1h DST transition gap (day boundaries at 00:00 / 23:59 are always safe).
 */
function zonedWallClockToUtc(
  y: number, mo: number, d: number, hh: number, mm: number, ss: number, ms: number,
): Date {
  const guess = Date.UTC(y, mo - 1, d, hh, mm, ss, ms);
  const offset = tzOffsetMs(getOrgTimezone(), new Date(guess));
  return new Date(guess - offset);
}

function parseDay(dateStr: string): { y: number; mo: number; d: number } {
  const m = DAY_RE.exec(dateStr);
  if (!m) throw new Error(`Invalid date format: ${dateStr}`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  // Reject impossible calendar dates (e.g. 2026-02-30, 2026-04-31, 2026-13-01)
  // BEFORE they silently roll over in Date.UTC — otherwise zurichDayStartUtc /
  // zurichDayEndUtc would bucket an out-of-range day into the wrong period.
  if (mo < 1 || mo > 12 || d < 1 || d > 31) {
    throw new Error(`Invalid calendar date: ${dateStr}`);
  }
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) {
    throw new Error(`Invalid calendar date: ${dateStr}`);
  }
  return { y, mo, d };
}

/**
 * B21: the UTC instant at the START of a `YYYY-MM-DD` day in Europe/Zurich.
 * Endpoints that compare a `timestamptz` (e.g. activities `activity_time`) must
 * bound on the local day, not `T00:00:00Z` — otherwise activities logged
 * 00:00–02:00 local on a boundary day land in the wrong period (same class as
 * the PR #636 child-state fix).
 */
export function zurichDayStartUtc(dateStr: string): Date {
  const { y, mo, d } = parseDay(dateStr);
  return zonedWallClockToUtc(y, mo, d, 0, 0, 0, 0);
}

/** B21: the UTC instant at the END (23:59:59.999 local) of a Zurich day. */
export function zurichDayEndUtc(dateStr: string): Date {
  const { y, mo, d } = parseDay(dateStr);
  return zonedWallClockToUtc(y, mo, d, 23, 59, 59, 999);
}

const MONTH_RE = /^(\d{4})-(\d{2})$/;

function parseMonth(month: string): Date {
  // `YYYY-MM` → first day of that month, local time. Guard against malformed
  // URL params (e.g. `2026-13`, `abc`) so callers like `monthDayBounds` never
  // produce an invalid Date that throws when formatted.
  const match = MONTH_RE.exec(month);
  if (!match) throw new Error(`Invalid month format: ${month}`);
  const m = Number(match[2]);
  if (m < 1 || m > 12) throw new Error(`Invalid month value: ${month}`);
  return new Date(Number(match[1]), m - 1, 1);
}
